import type { Project, WallInput } from "@tasduvar/engine";
import { t } from "../../i18n";

/** Rapora basılan "kabul ve dikkat" maddesi. warn: sonucu kesinleştirmeyen eksik girdi (kapakta tam metin). */
export type AdvisoryId = "seismic-default" | "seismic-off" | "backfill-cohesion" | "low-phi-drained" | "bearing-computed";
export interface Advisory {
  id: AdvisoryId; severity: "warn" | "info"; text: string;
  /** maddenin geçerli olduğu duvar adları; `allWalls` ise tüm rapordaki duvarlar */
  walls: string[]; allWalls: boolean;
}

export type WallAdvisory = Omit<Advisory, "walls" | "allWalls">;

/** Program varsayılanı deprem girdisi (makeDefaultWall: SS 1,00 · S1 0,25 · ZC) ve konum yok → AFAD değeri girilmemiş. */
export function isDefaultSeismicInput(wall: WallInput, project: Project): boolean {
  const s = wall.seismic;
  return s.enabled && s.inputMode === "map" && s.SS === 1 && s.S1 === 0.25 && s.siteClass === "ZC" && !project?.info?.location;
}

/** Tek duvarın maddeleri (sıra: deprem → dolgu → temel zemini → taşıma gücü). */
export function wallAdvisories(wall: WallInput, project: Project): WallAdvisory[] {
  const out: WallAdvisory[] = [];
  if (isDefaultSeismicInput(wall, project)) out.push({ id: "seismic-default", severity: "warn", text: t("Deprem girdisi program varsayılanı (SS 1,00 · S1 0,25 · ZC, yüksek deprem varsayımı). Proje konumunun AFAD TDTH değerleri girilmeden rapor kesin değildir.") });
  if (!wall.seismic.enabled) out.push({ id: "seismic-off", severity: "info", text: t("Deprem hesabı yapılmadı (kullanıcı seçimi).") });
  if (wall.backfill.layers.some((L) => L.c > 0)) out.push({ id: "backfill-cohesion", severity: "info", text: t("Dolgu kohezyonu itkiyi azaltır; tutucu tarafta c = 0 alınması önerilir.") });
  if (wall.foundationSoil.drained && wall.foundationSoil.phi < 15) out.push({ id: "low-phi-drained", severity: "info", text: t("Temel zemini φ < 15° drenajlı alındı; değer drenajsız deneyden geldiyse drenajsız (cu) hesap gerekir.") });
  if (wall.foundationSoil.bearing.mode === "computed") out.push({ id: "bearing-computed", severity: "info", text: t("Zemin emniyet gerilmesi etüt raporundan verilmedi; taşıma gücü zemin parametrelerinden hesaplandı (TBDY Denk. 16.8a).") });
  return out;
}

/** Rapordaki duvarların maddeleri birleşik: aynı madde birden çok duvardaysa bir kez (duvar adlarıyla; tümündeyse adsız). */
export function reportAdvisories(project: Project, walls: readonly { wall: WallInput }[] = project.walls.map((wall) => ({ wall }))): Advisory[] {
  const byId = new Map<AdvisoryId, Advisory>();
  for (const { wall } of walls) {
    for (const a of wallAdvisories(wall, project)) {
      const cur = byId.get(a.id);
      if (cur) cur.walls.push(wall.name); else byId.set(a.id, { ...a, walls: [wall.name], allWalls: false });
    }
  }
  const list = [...byId.values()].map((a) => ({ ...a, allWalls: a.walls.length === walls.length }));
  return list.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "warn" ? -1 : 1));
}

/** Basılacak satır: "⚠ " (warn) + metin + (tüm duvarlarda değilse) duvar adları. */
export function advisoryLine(a: Advisory): string {
  return `${a.severity === "warn" ? "⚠ " : ""}${a.text}${a.allWalls ? "" : ` (${a.walls.join(", ")})`}`;
}

/** Kapak kutusu başlığı: "⚠ Dikkat: N kabul/uyarı — bkz. Uyarılar ve kabuller". */
export function advisoryCoverTitle(n: number): string {
  return `⚠ ${t("Dikkat:")} ${n} ${t("kabul/uyarı — bkz. Uyarılar ve kabuller")}`;
}
